<template>
  <ul class="plans" role="radiogroup">
    <li v-for="tier in tiers" :key="tier.period" class="plans__item">
      <button
        type="button"
        role="radio"
        class="plan"
        :class="{ 'plan--selected': selected?.period === tier.period }"
        :aria-checked="selected?.period === tier.period"
        @click="$emit('select', tier)"
      >
        <span class="plan__label">{{ t(tier.labelKey) }}</span>
        <span class="plan__amount">{{ formatPrice(tier.amount, currency, locale) }}</span>
        <span class="plan__period">{{ t(tier.periodKey) }}</span>
      </button>
    </li>
  </ul>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { formatPrice } from '@/functions/format'

defineProps({
  tiers: { type: Array, default: () => [] },
  selected: { type: Object, default: null },
  currency: { type: String, default: '' }
})
defineEmits(['select'])

const brandStore = useBrandStore()
const { locale } = storeToRefs(brandStore)
const { t } = brandStore
</script>

<style scoped>
.plans {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--spacing-sm);
}

.plans__item {
  display: flex;
  min-width: 0;
}

.plan {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: var(--spacing-md) var(--spacing-xs);
  background-color: var(--color-surface);
  border: 2px solid var(--color-surface-border);
  border-radius: var(--radius-lg);
  color: var(--color-text);
  text-align: center;
  transition: border-color var(--transition-fast), background-color var(--transition-fast), transform var(--transition-fast);
}

.plan:active {
  transform: scale(0.98);
}

.plan--selected {
  border-color: var(--color-primary);
  background-color: var(--color-surface-raised);
}

.plan:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.plan__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
}

.plan__amount {
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--color-text);
  white-space: nowrap;
}

.plan--selected .plan__amount {
  color: var(--color-primary);
}

.plan__period {
  font-size: 0.72rem;
  color: var(--color-text-secondary);
}

@media (max-width: 360px) {
  .plan__amount {
    font-size: 1.15rem;
  }
}
</style>
